import {ChevronRightIcon, DashIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {useCallback, useId, useLayoutEffect, useRef, useState, type ReactNode} from 'react'

import styles from './UserChoiceCard.module.css'

export interface UserChoiceCardProps {
  /** Card title shown in the header. */
  title: ReactNode
  /** Optional muted text shown below the title. */
  subtitle?: ReactNode
  /** Additional controls rendered in the header. */
  headerButtons?: ReactNode
  fullWidthTitle?: boolean
  /** Keeps a compact header above the scroll area and retains collapsed content. */
  fixedHeader?: boolean
  /** Keeps content expanded and omits the collapse control when false. */
  collapsible?: boolean
  /** Preview content rendered above the choices. */
  previewContent?: ReactNode
  /** Choice rows or other interactive content. */
  children?: ReactNode
  /** Controls pinned below the scrollable card content. */
  footerButtons?: ReactNode
  /** Removes the outer card chrome when nested inside another card. */
  embedded?: boolean
  /** Controlled expanded state. */
  isExpanded?: boolean
  /** Called when the expand or collapse control is activated. */
  onToggle?: () => void
}

/** Card wrapper for active interactive user prompts. */
export function UserChoiceCard({
  title,
  subtitle,
  headerButtons,
  fullWidthTitle = false,
  fixedHeader = false,
  collapsible = true,
  previewContent,
  children,
  footerButtons,
  embedded = false,
  isExpanded: isExpandedProp,
  onToggle,
}: UserChoiceCardProps) {
  const [isExpandedInternal, setIsExpandedInternal] = useState(true)
  const isControlled = isExpandedProp !== undefined
  const isExpanded = !collapsible || (isControlled ? isExpandedProp : isExpandedInternal)
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollPositionRef = useRef(0)
  const contentId = useId()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)
  const [canScroll, setCanScroll] = useState(false)

  const checkScroll = useCallback(() => {
    const element = scrollRef.current
    if (!element) return

    setIsScrolled(element.scrollTop > 0)
    setCanScroll(element.scrollHeight > element.clientHeight)
    setIsScrolledToBottom(element.scrollTop + element.clientHeight >= element.scrollHeight - 1)
  }, [])

  useLayoutEffect(() => {
    if (fixedHeader && isExpanded && scrollRef.current) {
      scrollRef.current.scrollTo({top: scrollPositionRef.current})
    }
    checkScroll()
  }, [checkScroll, fixedHeader, isExpanded])

  const handleScroll = () => {
    if (fixedHeader && !isExpanded) return
    if (fixedHeader && scrollRef.current) scrollPositionRef.current = scrollRef.current.scrollTop
    checkScroll()
  }

  const handleToggle = () => {
    if (!isControlled) setIsExpandedInternal(current => !current)
    onToggle?.()
  }

  const header = !embedded ? (
    <div className={clsx(styles.Header, fullWidthTitle && styles.fullWidthTitle, fixedHeader && styles.FixedHeader)}>
      <div className={styles.HeaderText}>
        <div className={styles.Title}>{title}</div>
        {subtitle ? <div className={styles.Subtitle}>{subtitle}</div> : null}
      </div>
      {headerButtons || collapsible ? (
        <div className={styles.HeaderActions}>
          {headerButtons}
          {collapsible ? (
            <button
              type="button"
              className={styles.CollapseButton}
              onClick={handleToggle}
              aria-label={
                fixedHeader
                  ? isExpanded
                    ? 'Collapse question'
                    : 'Expand question'
                  : isExpanded
                    ? 'Collapse'
                    : 'Expand'
              }
              aria-controls={fixedHeader ? (footerButtons ? `${contentId} ${contentId}-footer` : contentId) : undefined}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <DashIcon size={16} /> : <ChevronRightIcon size={16} />}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  ) : null

  return (
    <div className={clsx(styles.Card, embedded && styles.embedded)}>
      {fixedHeader ? header : null}
      <div
        id={fixedHeader ? contentId : undefined}
        ref={scrollRef}
        className={styles.ScrollArea}
        hidden={fixedHeader && !isExpanded}
        onScroll={handleScroll}
      >
        {isExpanded && canScroll && isScrolled ? <div className={styles.GradientTop} /> : null}
        {!fixedHeader ? header : null}
        {isExpanded || fixedHeader ? (
          <>
            {previewContent ? <div className={styles.PreviewContent}>{previewContent}</div> : null}
            {children ? <div className={styles.ChildrenContent}>{children}</div> : null}
            {canScroll && !isScrolledToBottom ? <div className={styles.GradientBottom} /> : null}
          </>
        ) : null}
      </div>
      {(isExpanded || fixedHeader) && footerButtons ? (
        <div
          id={fixedHeader ? `${contentId}-footer` : undefined}
          className={clsx(styles.Footer, canScroll && styles.footerBorder)}
          hidden={!isExpanded}
        >
          <div className={styles.FooterButtons}>{footerButtons}</div>
        </div>
      ) : null}
    </div>
  )
}
